import React, { useState, useEffect, useMemo, useContext } from "react";
import { useLocation } from "react-router-dom";
import "../styles/Products.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight, faTimes } from "@fortawesome/free-solid-svg-icons";

// Import category JSONs
import menProducts from "../products/men-products.json";
import womenProducts from "../products/women-products.json";
import kidsProducts from "../products/kids-products.json";
import electronicsProducts from "../products/electronics-products.json";
import beautyProducts from "../products/beauty-products.json";
import jewelleryProducts from "../products/jewellery-products.json";

import { CartContext } from "../context/CartContext";

function Products() {
  const location = useLocation();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [imageIndex, setImageIndex] = useState(0);
  const [favourites, setFavourites] = useState([]);
  const [showFavourites, setShowFavourites] = useState(false);

  const { cartItems, addToCart } = useContext(CartContext);

  // Build allProducts with uniqueId per category
  const allProducts = useMemo(() => {
    const mapWithUniqueId = (arr, category) =>
      (arr || []).map((p) => ({ ...p, category, uniqueId: `${category}-${p.id}` }));

    return [
      ...mapWithUniqueId(menProducts.men, "men"),
      ...mapWithUniqueId(womenProducts.women, "women"),
      ...mapWithUniqueId(kidsProducts.kids, "kids"),
      ...mapWithUniqueId(electronicsProducts.electronics, "electronics"),
      ...mapWithUniqueId(beautyProducts.beauty, "beauty"),
      ...mapWithUniqueId(jewelleryProducts.jewellery, "jewellery"),
    ];
  }, []);

  // Load favourites from localStorage
  useEffect(() => {
    if (allProducts.length === 0) return;
    
    const saved = JSON.parse(localStorage.getItem("favourites")) || [];
    const restored = saved
      .map((s) => {
        if (s.uniqueId) {
          const match = allProducts.find((p) => p.uniqueId === s.uniqueId);
          if (match) return match;
        }
        if (s.id != null && s.category) {
          const match = allProducts.find(
            (p) => String(p.id) === String(s.id) && p.category === s.category
          );
          if (match) return match;
        }
        if (s.id != null) {
          const match = allProducts.find((p) => String(p.id) === String(s.id));
          if (match) return match;
        }
        if (s.name) {
          const match = allProducts.find((p) => p.name === s.name);
          if (match) return match;
        }
        return {
          ...s,
          uniqueId: s.uniqueId || (s.category && s.id ? `${s.category}-${s.id}` : `unknown-${s.id ?? Math.random()}`),
        };
      })
      .filter(Boolean);

    setFavourites(restored);
  }, [allProducts]);

  // Persist favourites
  useEffect(() => {
    localStorage.setItem("favourites", JSON.stringify(favourites));
  }, [favourites]);

  // Handle query params / navigation state
  useEffect(() => {
    const st = location.state;

    // If navigation state has openProduct, try to find and open that product
    if (st && st.openProduct) {
      const incoming = st.openProduct;
      const incomingCategory = st.category || incoming.category || null;

      let match =
        (incoming.uniqueId && allProducts.find((p) => p.uniqueId === incoming.uniqueId)) ||
        (incoming.id != null &&
          incomingCategory &&
          allProducts.find((p) => String(p.id) === String(incoming.id) && p.category === incomingCategory)) ||
        (incoming.id != null && allProducts.find((p) => String(p.id) === String(incoming.id))) ||
        (incoming.name && allProducts.find((p) => p.name === incoming.name));

      // If found, open that product and set category if provided
      if (match) {
        setSelectedCategory(match.category || (incomingCategory || "all"));
        setShowFavourites(false);
        setSelectedProduct(match);
        setImageIndex(0);
        return;
      } else {
        if (incomingCategory) {
          setSelectedCategory(incomingCategory);
          setShowFavourites(false);
        }
      }
    }

    // Parse query params
    const queryParams = new URLSearchParams(location.search);
    const cat = queryParams.get("category");
    const productParam = queryParams.get("product");

    // If category param exists, set it
    if (cat && cat !== "all") {
      setSelectedCategory(cat);
      setShowFavourites(false);
    } else {
      setSelectedCategory("all");
      setShowFavourites(false);
    }

    // If product param exists, try to find and open that product
    if (productParam) {
      const prod =
        allProducts.find((p) => p.uniqueId === productParam) ||
        allProducts.find((p) => String(p.id) === productParam);
      if (prod) {
        setSelectedProduct(prod);
        setImageIndex(0);
      }
    }

    // If search param exists, set it
    const searchParam = new URLSearchParams(location.search).get("search");
    if (searchParam) {
      setSearchTerm(searchParam.toLowerCase());
    } else {
      setSearchTerm("");
    }

  }, [location, allProducts]);

  // derive displayedProducts
  const displayedProducts = useMemo(() => {
    let products = showFavourites ? favourites : allProducts;

    if (selectedCategory !== "all" && !showFavourites) {
      products = products.filter((p) => p.category === selectedCategory);
    }

    if (searchTerm.trim() !== "") {
      const term = searchTerm.trim().toLowerCase();
      products = products.filter((p) => p.name.toLowerCase().includes(term));
    }

    return products;
  }, [showFavourites, favourites, selectedCategory, allProducts, searchTerm]);


  // Toggle favourite
  const toggleFavourite = (product) => {
    setFavourites((prev) => {
      const exists = prev.find((p) => p.uniqueId === product.uniqueId);
      if (exists) return prev.filter((p) => p.uniqueId !== product.uniqueId);
      return [...prev, product];
    });
  };

  const removeFavourite = (uniqueId) => {
    setFavourites((prev) => prev.filter((p) => p.uniqueId !== uniqueId));
  };

  return (
    <div className="products-page">
      <section className="products-hero">
        <h1>Explore Our Collections</h1>
        <p>Find your perfect match from a wide range of products!</p>
      </section>

      {/* Filters */}
      <div className="filters-bar">
        <div className="category-filters">
          {["all", "men", "women", "kids", "electronics", "beauty", "jewellery"].map((cat) => (
            <button
              key={cat}
              className={`filter-btn ${selectedCategory === cat && !showFavourites ? "active" : ""}`}
              onClick={() => {
                setShowFavourites(false);
                setSelectedCategory(cat);
              }}
            >
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </button>
          ))}
        </div>
        <button
          className={`filter-btn favourites-btn ${showFavourites ? "active" : ""}`}
          onClick={() => setShowFavourites(true)}
        >
          Favourites
        </button>
      </div>

      {/* Products / Favourites grid */}
      {showFavourites ? (
        <div className="favourites-grid">
          {displayedProducts.length > 0 ? (
            displayedProducts.map((product) => (
              <div key={product.uniqueId} className="favourites-card">
                <button className="remove-fav-btn" onClick={() => removeFavourite(product.uniqueId)}>
                  <FontAwesomeIcon icon={faTimes} />
                </button>
                <div className="fav-img-section">
                  <img
                    src={product.images?.[0] ?? "placeholder.png"}
                    alt={product.name}
                    onClick={() => setSelectedProduct(product)}
                  />
                </div>
                <div className="favourites-details">
                  <h3>{product.name}</h3>
                  <p>Price: ₹{product.price}</p>
                  <div className="card-buttons">
                    <button onClick={() => setSelectedProduct(product)}>Know More</button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="no-results">No favourites yet.</p>
          )}
        </div>
      ) : (
        <div className="products-grid">
          {displayedProducts.map((product) => (
            <div key={product.uniqueId} className="product-card">
              <img
                src={product.images?.[0] ?? "placeholder.png"}
                alt={product.name}
                onClick={() => setSelectedProduct(product)}
              />
              <h3>{product.name}</h3>
              <p>₹{product.price}</p>
              <button className="know-more-btn" onClick={() => setSelectedProduct(product)}>
                Know More
              </button>
              <div className="card-buttons">
                <button onClick={() => addToCart(product)}>
                  {cartItems.some((p) => p.uniqueId === product.uniqueId) ? "Added" : "Add to Cart"}
                </button>
                <button onClick={() => toggleFavourite(product)}>
                  {favourites.some((p) => p.uniqueId === product.uniqueId) ? "❤️ Added" : "🤍 Favourite"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Product Modal */}
      {selectedProduct && (
        <div className="product-modal" onClick={() => setSelectedProduct(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="carousel-wrapper">
              <img
                src={selectedProduct.images?.[imageIndex] ?? "placeholder.png"}
                alt={selectedProduct.name}
                className="main-image"
              />
              {selectedProduct.images && selectedProduct.images.length > 1 && (
                <>
                  <button
                    className="image-nav left"
                    onClick={() =>
                      setImageIndex((prev) =>
                        prev === 0 ? selectedProduct.images.length - 1 : prev - 1
                      )
                    }
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  <button
                    className="image-nav right"
                    onClick={() =>
                      setImageIndex((prev) =>
                        prev === selectedProduct.images.length - 1 ? 0 : prev + 1
                      )
                    }
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </>
              )}
            </div>

            <div className="modal-details modal-center">
              <h2>{selectedProduct.name}</h2>
              <p className="modal-price">₹{selectedProduct.price}</p>
              <p className="modal-rating">
                ⭐ {selectedProduct.rating} ({selectedProduct.reviews} reviews)
              </p>
              <p>
                Colors:{" "}
                {Array.isArray(selectedProduct.colors)
                  ? selectedProduct.colors.join(", ")
                  : selectedProduct.colors}
              </p>
              <p>Stock Available: {selectedProduct.count}</p>
              <div className="modal-buttons">
                <button onClick={() => addToCart(selectedProduct)}>
                  {cartItems.some((p) => p.uniqueId === selectedProduct.uniqueId) ? "Added" : "Add to Cart"}
                </button>
                <button onClick={() => toggleFavourite(selectedProduct)}>
                  {favourites.some((p) => p.uniqueId === selectedProduct.uniqueId) ? "❤️ Added" : "🤍 Favourite"}
                </button>
              </div>
              <button className="close-modal" onClick={() => setSelectedProduct(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Products;